// LoanHistory.js for the borrower

import React, { useState, useEffect } from 'react';
import { useGetLoansByBorrowerQuery } from '../../services/loanApiSlice';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  DollarSign,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  FileText,
  RefreshCw,
  ChevronRight,
  TrendingUp,
  ShieldCheck,
  PieChart,
  CreditCard,
  ArrowUpRight,
  Eye,
  Percent,
  Search,
  X
} from 'lucide-react';

const LoanHistory = () => {
  const userData = JSON.parse(localStorage.getItem('user') || '{}');
  const borrowerId = userData.userId || 2;
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredLoans, setFilteredLoans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const { 
    data: loans = [], 
    isLoading: apiLoading, 
    isError, 
    refetch 
  } = useGetLoansByBorrowerQuery(borrowerId);

  // Helper functions - defined before use
  const calculateMonthlyPayment = (principal, annualRate, months) => {
    if (!principal || !annualRate || !months) return 0;
    const totalInterest = principal * (annualRate / 100) * (months / 12);
    const monthlyPayment = (principal + totalInterest) / months;
    return monthlyPayment.toFixed(2);
  };

  const calculateTotalPayable = (principal, annualRate, months) => {
    const monthlyPayment = parseFloat(calculateMonthlyPayment(principal, annualRate, months));
    return (monthlyPayment * months).toFixed(2);
  };

  // Compute stats
  const totalPrincipal = loans.reduce((sum, loan) => sum + (loan.principalAmount || 0), 0);
  const activeLoans = loans.filter(l => l.status === 'Active' || l.status === 'Approved');
  const activeCount = activeLoans.length;
  const totalMonthlyPayment = activeLoans.reduce((sum, loan) => {
    const mp = parseFloat(calculateMonthlyPayment(loan.principalAmount, loan.interestRate, loan.termMonths)) || 0;
    return sum + mp;
  }, 0);
  const avgInterestRate = loans.length 
    ? (loans.reduce((sum, loan) => sum + (loan.interestRate || 0), 0) / loans.length).toFixed(1) 
    : 0;

  // Filter loans based on status and search query
  useEffect(() => {
    if (!apiLoading) {
      setIsLoading(false);
      let filtered = [...loans];

      // 1. Apply status filter
      switch(filter) {
        case 'active':
          filtered = loans.filter(loan => loan.status === 'Active' || loan.status === 'Approved');
          break;
        case 'overdue':
          filtered = loans.filter(loan => loan.status === 'Overdue');
          break;
        case 'closed':
          filtered = loans.filter(loan => loan.status === 'Closed' || loan.status === 'Paid');
          break;
        default:
          filtered = loans;
      }

      // 2. Apply search query if present
      if (searchQuery.trim() !== '') {
        const query = searchQuery.trim().toLowerCase();
        const queryAsNumber = parseFloat(query);
        const isNumericQuery = !isNaN(queryAsNumber) && queryAsNumber.toString() === query; // ensure whole query is a number

        filtered = filtered.filter(loan => {
          // Search by name (case‑insensitive)
          const nameMatch = loan.loanProductName?.toLowerCase().includes(query);

          // Search by loan ID (as string)
          const idMatch = loan.loanId?.toString().toLowerCase().includes(query);

          // If the query is numeric, also check principal and total payable
          if (isNumericQuery) {
            const principal = loan.principalAmount || 0;
            const totalPayable = parseFloat(calculateTotalPayable(loan.principalAmount, loan.interestRate, loan.termMonths)) || 0;
            const principalMatch = Math.abs(principal - queryAsNumber) < 0.01;
            const totalMatch = Math.abs(totalPayable - queryAsNumber) < 0.01;
            return nameMatch || idMatch || principalMatch || totalMatch;
          }

          // Non‑numeric query: only string fields
          return nameMatch || idMatch;
        });
      }

      setFilteredLoans(filtered);
    }
  }, [loans, filter, searchQuery, apiLoading]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'Active': { color: 'bg-emerald-500/10 text-emerald-600 border-emerald-200', icon: CheckCircle },
      'Approved': { color: 'bg-emerald-500/10 text-emerald-600 border-emerald-200', icon: CheckCircle },
      'Pending': { color: 'bg-amber-500/10 text-amber-600 border-amber-200', icon: Clock },
      'Overdue': { color: 'bg-rose-500/10 text-rose-600 border-rose-200', icon: AlertCircle },
      'Closed': { color: 'bg-gray-500/10 text-gray-600 border-gray-200', icon: XCircle },
      'Paid': { color: 'bg-blue-500/10 text-blue-600 border-blue-200', icon: ShieldCheck },
      'Rejected': { color: 'bg-rose-500/10 text-rose-600 border-rose-200', icon: XCircle },
    };

    const statusInfo = statusMap[status] || { color: 'bg-gray-500/10 text-gray-600 border-gray-200', icon: FileText };
    const IconComponent = statusInfo.icon;

    return (
      <div className={`inline-flex items-center px-3 py-1.5 rounded-lg border ${statusInfo.color}`}>
        <IconComponent className="w-3.5 h-3.5 mr-1.5" />
        <span className="text-xs font-semibold">{status}</span>
      </div>
    );
  };

  const handleViewSchedules = (loanId) => {
    localStorage.setItem('selectedLoanId', loanId);
    navigate('/repayment');
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          </div>
          <p className="text-gray-500 text-sm">Loading loans...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-rose-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Connection Error</h3>
          <p className="text-gray-500 text-sm mb-4">Unable to fetch loan data. Please check your connection.</p>
          <button
            onClick={refetch}
            className="px-5 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium text-sm"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header with title and refresh */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mr-4 shadow-lg shadow-blue-500/20">
              <CreditCard className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Your Loans</h1>
              <p className="text-sm text-gray-500">Manage and track your loan portfolio</p>
            </div>
          </div>
          <button
            onClick={refetch}
            className="p-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Modern Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          {/* ... (stat cards unchanged) ... */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-sm">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <ArrowUpRight className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-sm text-gray-500 mb-1">Total Borrowed</p>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalPrincipal)}</p>
            <p className="text-xs text-gray-400 mt-1">Across all loans</p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-sm">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <ArrowUpRight className="w-5 h-5 text-emerald-500" />
            </div>
            <p className="text-sm text-gray-500 mb-1">Active Loans</p>
            <p className="text-2xl font-bold text-gray-900">{activeCount}</p>
            <p className="text-xs text-gray-400 mt-1">Currently {activeCount === 1 ? 'repaying' : 'repaying'}</p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-sm">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <ArrowUpRight className="w-5 h-5 text-purple-500" />
            </div>
            <p className="text-sm text-gray-500 mb-1">Monthly Payment</p>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalMonthlyPayment)}</p>
            <p className="text-xs text-gray-400 mt-1">Total for active loans</p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center shadow-sm">
                <Percent className="w-6 h-6 text-white" />
              </div>
              <ArrowUpRight className="w-5 h-5 text-amber-500" />
            </div>
            <p className="text-sm text-gray-500 mb-1">Avg Interest Rate</p>
            <p className="text-2xl font-bold text-gray-900">{avgInterestRate}%</p>
            <p className="text-xs text-gray-400 mt-1">Across all loans</p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-2xl">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, ID, or amount..."
              className="block w-full pl-10 pr-24 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />

            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute inset-y-0 right-0 pr-2 flex items-center"
              >
                <span className="px-3 py-1 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors">
                  Clear
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Filter Chips */}
        <div className="flex items-center space-x-2 mb-6 overflow-x-auto pb-2">
          {[
            { key: 'all', label: 'All Loans', count: loans.length },
            { key: 'active', label: 'Active', count: loans.filter(l => l.status === 'Active' || l.status === 'Approved').length },
            { key: 'overdue', label: 'Overdue', count: loans.filter(l => l.status === 'Overdue').length },
            { key: 'closed', label: 'Closed', count: loans.filter(l => l.status === 'Closed' || l.status === 'Paid').length }
          ].map((filterType) => (
            <button
              key={filterType.key}
              onClick={() => setFilter(filterType.key)}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 whitespace-nowrap ${
                filter === filterType.key
                  ? 'bg-blue-500 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {filterType.label}
              <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                filter === filterType.key
                  ? 'bg-white/30'
                  : 'bg-gray-300 text-gray-700'
              }`}>
                {filterType.count}
              </span>
            </button>
          ))}
        </div>

        {/* Loan Cards */}
        {filteredLoans.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No loans found</h3>
            <p className="text-gray-500 text-sm">
              {searchQuery
                ? `No results matching "${searchQuery}"`
                : filter === 'all'
                ? "You don't have any loans yet"
                : `No ${filter} loans available`
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredLoans.map((loan) => {
              const monthlyPayment = calculateMonthlyPayment(
                loan.principalAmount,
                loan.interestRate,
                loan.termMonths
              );
              
              const totalPayable = calculateTotalPayable(
                loan.principalAmount,
                loan.interestRate,
                loan.termMonths
              );

              const isActive = loan.status === 'Active' || loan.status === 'Approved';

              return (
                <div
                  key={loan.loanId}
                  className={`group bg-white rounded-2xl border ${
                    isActive ? 'border-gray-200 hover:border-blue-200 hover:shadow-xl' : 'border-gray-100 opacity-80'
                  } transition-all duration-300 overflow-hidden`}
                >
                  <div className="p-6">
                    {/* Card Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mr-3 ${
                          isActive 
                            ? 'bg-gradient-to-br from-blue-500 to-blue-600' 
                            : 'bg-gray-200'
                        }`}>
                          <TrendingUp className={`w-6 h-6 ${isActive ? 'text-white' : 'text-gray-500'}`} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{loan.loanProductName || 'Personal Loan'}</h3>
                          <p className="text-xs text-gray-500">ID: {loan.loanId}</p>
                        </div>
                      </div>
                      {getStatusBadge(loan.status)}
                    </div>

                    {/* Amount Section */}
                    <div className="mb-6">
                      <div className="flex items-end justify-between mb-2">
                        <div>
                          <p className="text-sm text-gray-500">Principal</p>
                          <p className="text-2xl font-bold text-gray-900">
                            {formatCurrency(loan.principalAmount)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-500">Total Payable</p>
                          <p className="text-lg font-semibold text-blue-600">
                            {formatCurrency(totalPayable)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Loan Details Grid */}
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="space-y-4">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center mr-3">
                            <Calendar className="w-4 h-4 text-blue-500" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Start Date</p>
                            <p className="text-sm font-medium text-gray-900">
                              {formatDate(loan.startDate)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center mr-3">
                            <DollarSign className="w-4 h-4 text-emerald-500" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Monthly</p>
                            <p className="text-sm font-medium text-gray-900">
                              {formatCurrency(monthlyPayment)}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center mr-3">
                            <Clock className="w-4 h-4 text-purple-500" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Term</p>
                            <p className="text-sm font-medium text-gray-900">
                              {loan.termMonths} months
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center mr-3">
                            <PieChart className="w-4 h-4 text-orange-500" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Rate</p>
                            <p className="text-sm font-medium text-gray-900">
                              {loan.interestRate}%
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action Button */}
                    {isActive && (
                      <button
                        onClick={() => handleViewSchedules(loan.loanId)}
                        className="w-full py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium hover:from-blue-600 hover:to-blue-700 transition-all duration-200 flex items-center justify-center group/btn shadow-sm hover:shadow-md"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Schedules
                        <ChevronRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-100">
          <div className="flex items-center justify-between text-sm text-gray-500">
            <div>
              Showing <span className="font-semibold text-gray-700">{filteredLoans.length}</span> of{' '}
              <span className="font-semibold text-gray-700">{loans.length}</span> loans
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-emerald-500 rounded-full mr-1 animate-pulse"></div>
              Live data
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoanHistory;